<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />

    <!-- External Form -->
    <v-dialog v-model="externalPolicyDialog" persistent width="500">
      <v-card>
        <v-card-title class="text-title mb-4">
          Select External Loan Product
        </v-card-title>

        <v-card-text>
          <ValidationObserver ref="externalDistributionObserver">
            <v-row dense justify="center">
              <v-col cols="12" lg="12" v-if="!editingExternalDistribution">
                <ValidationProvider
                  name="Select Person"
                  rules="required"
                  events="['blur']"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    outlined
                    label="Select Person"
                    dense
                    background-color="white"
                    :error-messages="errors"
                    :items="persons"
                    item-value="personId"
                    item-text="name"
                    v-model="externalDistribution.personId"
                  >
                    <template v-slot:label>
                      Select Person
                      <span class="red--text">*</span>
                    </template>
                  </v-autocomplete>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" lg="12">
                <ValidationProvider
                  name="External Product"
                  rules="required"
                  events="['blur']"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    outlined
                    label="External Product"
                    dense
                    background-color="white"
                    :error-messages="errors"
                    :items="loan.loanProductDetails?.externalPolicies || []"
                    item-value="code"
                    item-text="name"
                    v-model="externalDistribution.code"
                  >
                    <template v-slot:label>
                      External Product
                      <span class="red--text">*</span>
                    </template>
                  </v-autocomplete>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  name="External Amount"
                  :rules="`required|min_value:${
                    selectedExternalProduct?.loanFloor || 0
                  }|max_value:${selectedExternalProduct?.loanCeiling || 0}`"
                  v-slot="{ errors }"
                >
                  <vuetify-money
                    type="number"
                    outlined
                    dense
                    required
                    label="External Loan Amount"
                    v-model="externalDistribution.loanAmount"
                    :error-messages="errors"
                    :options="VuetifyMoneyOptions"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  name="Interest"
                  :rules="`required|min_value:${
                    selectedExternalProduct?.minInterest
                  }|max_value:${selectedExternalProduct?.maxInterest || 100}`"
                  events="['blur']"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    outlined
                    type="number"
                    label="Annual Interest Rate"
                    dense
                    background-color="white"
                    :error-messages="errors"
                    v-model="externalDistribution.interest"
                  >
                    <template v-slot:label>
                      Annual Interest Rate
                      <span class="red--text">*</span>
                    </template>
                    <span slot="append" style="font-size: 1.2em">%</span>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="externalPolicyDialog = false"> Cancel </v-btn>
          <v-btn color="primary" text @click="saveExternalDistribution">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row align="center">
      <v-col cols="12">
        <!-- Internal Loan Distribution Table -->
        <v-simple-table id="internalTable" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <td colspan="5">
                  <span class="font-weight-bold">INTERNAL LOAN</span>
                </td>
              </tr>
              <tr>
                <th class="text-left">Name(s)</th>
                <th class="text-left">Editable Terms</th>
                <th class="text-left" v-if="internalDistributions.length > 1">
                  Payment Distribution
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(distribution, i) in internalDistributions" :key="i">
                <td>
                  {{ distribution.person?.firstName }}
                  {{ distribution.person?.lastName }} /
                  {{ distribution.person?.trn }}
                </td>
                <td rowspan="2" v-if="i == 0">
                  <ValidationObserver :ref="`loanDistributionObserver${i}`">
                    <v-row dense class="mt-3">
                      <v-col cols="6">
                        <ValidationProvider
                          name="Interest"
                          :rules="`required|min_value:0|max_value:${loan.originalTerms?.maxInterest}`"
                          events="['blur']"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            outlined
                            type="number"
                            label="Annual Interest Rate"
                            dense
                            background-color="white"
                            :disabled="i != 0 || isWaiverApproved('interest')"
                            :error-messages="errors"
                            @blur="onInterestChanged(distribution)"
                            v-model="distribution.interest"
                            :hint="`Member can get as low as ${loan?.maxTerms?.interest}%`"
                            persistent-hint
                          >
                            <template v-slot:label>
                              Annual Interest Rate
                              <span class="red--text">*</span>
                            </template>
                            <span slot="append" style="font-size: 1.2em"
                              >%</span
                            >
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="6">
                        <ValidationProvider
                          :name="`${loan.frequencyName || 'N/A'} Payment`"
                          :rules="`required|min_value:0|max_value:${
                            loan?.originalTerms?.loanCeiling || 0
                          }`"
                          v-slot="{ errors }"
                        >
                          <vuetify-money
                            type="number"
                            required
                            outlined
                            dense
                            :disabled="i != 0"
                            @blur="onPaymentAmountChanged(distribution, $event)"
                            :label="`${loan.frequencyName || 'N/A'} Payment`"
                            v-model="loan.internalFrequencyPayment"
                            :error-messages="errors"
                            :options="VuetifyMoneyOptions"
                          />
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </ValidationObserver>
                </td>
                <td>
                  <ValidationProvider
                    :name="`Distributed ${loan.frequencyName || 'N/A'} Payment`"
                    :rules="`required|min_value:0|max_value:${
                      loan.frequencyPayment || 0
                    }`"
                    v-if="internalDistributions.length > 1"
                    v-slot="{ errors }"
                  >
                    <vuetify-money
                      type="number"
                      class="mx-1 mt-3"
                      outlined
                      dense
                      @blur="
                        onInternalPaymentAmountChanged(distribution, $event)
                      "
                      :label="`${loan.frequencyName || 'N/A'} Payment*`"
                      v-model="distribution.frequencyPayment"
                      :error-messages="errors"
                      :options="VuetifyMoneyOptions"
                    />
                  </ValidationProvider>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>

      <v-col cols="12">
        <!-- External Loan Distribution Table -->
        <v-simple-table class="mt-5">
          <template v-slot:default>
            <thead>
              <tr>
                <td colspan="5">
                  <span class="font-weight-bold">EXTERNAL LOAN (S)</span>
                </td>
                <td class="d-flex justify-end px-0">
                  <v-btn
                    type="submit"
                    text
                    outlined
                    v-if="hasExternalProduct && persons.length"
                    color="primary"
                    class="text-capitalize shadow mx-1"
                    @click="openExternalDialog()"
                    >Add External Loan</v-btn
                  >
                </td>
              </tr>
              <tr>
                <th class="text-left">Name(s)</th>
                <th class="text-left">Product</th>
                <th class="text-left">Interest</th>
                <th class="text-left">Total Amount</th>
                <th class="text-left">Payment</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(distribution, i) in externalDistributions" :key="i">
                <td>
                  <span v-if="distribution.person"
                    >{{ distribution.person?.firstName }}
                    {{ distribution.person?.lastName }} /
                    {{ distribution.person?.trn }}</span
                  >
                </td>
                <td>
                  {{ distribution.externalTerms?.name }} ({{
                    distribution.externalTerms?.code
                  }})
                </td>
                <td>{{ asPercent(distribution.interest) }}</td>
                <td>{{ asMoney(distribution.loanAmount || 0) }}</td>
                <td>
                  {{ asMoney(distribution.frequencyPayment || 0.0) }}
                  {{ loan.frequencyName?.toUpperCase() }}
                </td>
                <td class="d-flex justify-end">
                  <div class="py-2">
                    <v-btn
                      class="mr-2 text-capitalize"
                      small
                      depressed
                      text
                      @click="removeDistribution(distribution)"
                    >
                      Delete
                    </v-btn>
                    <v-btn
                      class="text-capitalize"
                      color="primary"
                      outlined
                      small
                      text
                      @click="openExternalDialog(distribution)"
                    >
                      Edit
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>
  </div>
</template>

<style>
#internalTable td {
  border-bottom: 0px !important;
}
</style>
<script>
import PaymentCalculationService from '@/modules/loanpro/services/payment-calculation.service';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import PageMixin from '@/mixins/page.mixin';
import { v4 as uuidv4 } from 'uuid';

export default {
  mixins: [PageMixin],

  props: {
    /**
     * Loan details
     */
    loanDetails: {
      type: Object,
      default: () => ({
        loanTerms: {},
        loanPurposes: [],
        loanProductDetails: {
          maximumAge: 1
        }
      })
    }
  },

  watch: {
    /**
     * Watch loanDetails prop and update the local loan variable
     */
    loanDetails: {
      deep: true,
      immediate: true,
      handler: function (val) {
        this.loan = structuredClone(val);
      }
    }
  },

  data: () => ({
    VuetifyMoneyOptions: VuetifyMoneyOptions,

    loan: {},
    externalDistribution: {},
    externalPolicyDialog: false,
    editingExternalDistribution: false,

    excludedTypes: ['co-borrower', 'guarantor', 'co-maker'],

    paymentTimeout: null,
    confirmationTitle: '',
    confirmationMessage: ''
  }),

  computed: {
    /**
     * Gets all the internal loan distributions who is not in the exclude type list
     */
    loanDistributions: function () {
      const dists = [];
      let internalDistribution = {
        persons: []
      };

      this.loan?.loanDistributions
        ?.filter((el) => !this.excludedTypes.includes(el.personType))
        ?.forEach((el) => {
          if (el.distributionType === 'external') {
            dists.push(structuredClone(el));
          } else {
            internalDistribution.persons.push(structuredClone(el.person));

            internalDistribution = {
              ...internalDistribution,
              ...structuredClone(el),
              person: undefined
            };
          }
        });

      dists.unshift(structuredClone(internalDistribution));
      return dists || [];
    },

    /**
     * Gets all the external loan distributions
     */
    externalDistributions: function () {
      return this.loan?.loanDistributions
        ?.filter(
          (el) =>
            !this.excludedTypes.includes(el.personType) &&
            el.distributionType === 'external'
        )
        ?.map((el) => el);
    },

    /**
     * Gets all the internal loan distributions
     */
    internalDistributions: function () {
      const dist = this.loan?.loanDistributions
        ?.filter(
          (el) =>
            !this.excludedTypes.includes(el.personType) &&
            el.distributionType !== 'external'
        )
        ?.map((el) => el);

      return dist;
    },

    /**
     * Gets total internal distribution amount
     */
    totalInternalDistributions: function () {
      return (
        this.loan?.loanDistributions?.filter(
          (el) => el.distributionType == 'internal'
        ).length || 0
      );
    },

    /**
     * Return true if the selected product has an external product attached to it
     */
    hasExternalProduct: function () {
      return this.loan?.loanProductDetails?.externalPolicies?.length || false;
    },

    /**
     * Get the selected external product
     */
    selectedExternalProduct: function () {
      return (
        this.loan?.loanProductDetails?.externalPolicies?.find(
          (el) => el.code === this.externalDistribution.code
        ) || {}
      );
    },

    /**
     * Get all persons who are on the loan excluding the external distribution types
     */
    persons: function () {
      const personIds = this.loan.loanDistributions
        .filter((el) => el.distributionType === 'external')
        .map((el) => el.personId);

      return (
        this.loan?.loanDistributions
          ?.filter((el) => !personIds.includes(el.personId))
          ?.map((el) => ({
            personId: el.personId,
            person: el.person,
            name: `${el.person?.firstName} ${el.person?.lastName}`
          })) || []
      );
    }
  },

  methods: {
    /**
     * Return true if the loan progress is waiver approved
     */
    isWaiverApproved: function (termKey) {
      return (
        this.loan?.loanProgress == 'waiver approved' &&
        !!this.loan?.previousTermWaiver[termKey]
      );
    },

    /**
     * Validate loan distributions
     *
     * @returns { valid: Boolean, message: String | undefined }
     */
    async validate() {
      let i = 0;
      let valid = false;

      for (const _ of this.loanDistributions) {
        if (!this.$refs[`loanDistributionObserver${i}`]) continue;

        valid = await this.$refs[
          `loanDistributionObserver${i}`
        ]?.[0].validate();
        if (!valid) {
          return {
            valid,
            message:
              'The initial details section is invalid and require your attention.'
          };
        }
        i++;
      }

      /*const totalDistributions = this.loan
        ?.loanDistributions
        ?.filter(el => !this.excludedTypes.includes(el.personType))
        ?.filter(el => el.distributionType == "internal")
        ?.reduce((acc, cur) => acc += cur.loanAmount, 0)|| 0;

      if(totalDistributions !== this.loan.totalInternalLoanAmount) {
        return { valid, message: "Total loan distributions amount must equal ${this.loan.totalInternalLoanAmount}." }
      }*/

      return { valid: true };
    },

    /**
     * Gets call when loan distribution has been updated
     */
    onEmitUpdate() {
      /**
       * Triggered when any loan distribution has changed
       *
       * @event updated
       * @emit updated loan details
       */
      this.$emit('updated', this.loan);
    },

    /**
     * Gets call when the interest rate has changed
     *
     * @param { Object } item - loan distribution that has changed
     */
    onInterestChanged(item) {
      const index = this.loan.loanDistributions.findIndex(
        (el) =>
          el.personId === item.personId && el.distributionType === 'internal'
      );

      this.loan.loanDistributions[index].interest = item.interest;
      this.loan.interest = item.interest;

      this.onEmitUpdate();
    },

    /**
     * Gets call when the internal payment amount has changed
     *
     * @param { Object } item - loan distribution that has changed
     * @param { Number } value - new internal amount
     */
    onInternalPaymentAmountChanged(item, value) {
      const index = this.loan.loanDistributions.findIndex(
        (el) =>
          el.personId === item.personId && el.distributionType === 'internal'
      );

      this.loan.loanDistributions[index].frequencyPayment = value;

      this.onEmitUpdate();
    },

    /**
     * Gets call when the payment amount has changed
     *
     * @param { Object } item - loan distribution that has changed
     * @param { Number } value - new payment amount
     */
    onPaymentAmountChanged(item, value) {
      this.loan.internalFrequencyPayment = +value;
      const index = this.loan.loanDistributions.findIndex(
        (el) =>
          el.personId === item.personId && el.distributionType === 'internal'
      );

      const tenure = PaymentCalculationService.calculateTenure(
        +item.loanAmount,
        +this.loan.internalFrequencyPayment,
        +item.interest / 100 / this.loan.frequencyPeriod,
        this.loan.loanProductDetails.monthlyPaymentCalculation
      );
      this.loan.frequencyTenure = +Math.ceil(tenure);

      //store monthly tenure
      this.loan.tenure =
        (this.loan.frequencyTenure * 12) / this.loan.frequencyPeriod;

      const dist = this.loan.loanDistributions[index];
      dist.internalFrequencyPayment = item.internalFrequencyPayment;
      this.loan.loanDistributions.splice(index, 1, dist);

      this.onEmitUpdate();
    },

    /**
     * open external distribution form dialog
     *
     * @param { Object | null } item - loan distribution
     */
    openExternalDialog(item) {
      this.editingExternalDistribution = false;
      this.externalDistribution = {};
      this.externalPolicyDialog = true;
      if (item) {
        this.editingExternalDistribution = true;
        this.externalDistribution = {
          interest: item.interest,
          loanAmount: item.loanAmount,
          code: item.externalTerms?.code ?? ''
        };
      }
    },

    /**
     * remove an external distribution from loan
     *
     * @param { Object | null } item - the loan distribution to be removed
     */
    removeDistribution(item) {
      this.confirmationTitle = `Confirm Removal`;
      this.confirmationMessage = `Are you sure you want to remove this External Product?`;
      this.$refs.confirmationModal.show(async () => {
        const index = this.loan.loanDistributions.findIndex(
          (el) => el.id === item.id
        );
        this.loan.loanDistributions.splice(index, 1);

        this.onEmitUpdate();
      });
    },

    /**
     * Add an external distribution or updated the info for an existing external distribution
     */
    async saveExternalDistribution() {
      const valid = await this.$refs.externalDistributionObserver.validate();
      if (!valid) return;

      const policy = this.loan.loanProductDetails.externalPolicies.find(
        (el) => el.code === this.externalDistribution.code
      );

      const person = this.loan.loanDistributions
        .map((el) => el.person)
        .find((el) => el.id === this.externalDistribution.personId);

      this.externalDistribution.distributionType = 'external';
      this.externalDistribution.personType = 'borrower';
      this.externalDistribution.externalTerms = policy;
      this.externalDistribution = {
        ...this.externalDistribution,
        loanAmount: +this.externalDistribution.loanAmount,
        person
      };

      if (this.editingExternalDistribution) {
        const i = this.loan.loanDistributions.findIndex(
          (el) => el.id === this.externalDistribution.id
        );
        this.loan.loanDistributions.splice(
          i,
          1,
          structuredClone(this.externalDistribution)
        );
      } else {
        this.externalDistribution.id = uuidv4();
        this.loan.loanDistributions.push(
          structuredClone(this.externalDistribution)
        );
      }

      //Update Loan Details
      this.externalPolicyDialog = false;
      this.onEmitUpdate();
    }
  }
};
</script>
